"use client";

import {
  TypeCheckoutConfiguration,
  TypePackageAddOnItemSkeleton,
  TypePackageDetailItem,
  TypePackageModificationItem,
} from "@contentful/types";
import { faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckoutTotals } from "@yardzen/rest-api-client";
import { Button } from "@yardzen/ui/components/button";
import { Card } from "@yardzen/ui/components/card";
import { H5 } from "@yardzen/ui/components/h5";
import { Input } from "@yardzen/ui/components/input";
import { LineDivider } from "@yardzen/ui/components/line-divider";
import { Spinner } from "@yardzen/ui/components/spinner";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import Image from "next/image";
import { useState } from "react";
import { PackageDisplayAddOns } from "./PackageDisplayAddOns";

export function PackageDetailItemCheckout({
  packageDetailItem,
  currentAddons,
  packageModificationItem,
  paymentIntent,
  chooseAddonItem,
  isLoading = false,
  onApplyPromoCode,
  onClearPromoCode,
  currentPromoCode = "",
  promoCodeError = null,
  checkoutConfiguration,
  hideAddOns = false,
  allowPromoRemoval = true,
}: {
  packageDetailItem: TypePackageDetailItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  currentAddons: string[];
  packageModificationItem?: TypePackageModificationItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  /** The totals to render — a PaymentIntent response or the $0 quote. */
  paymentIntent: CheckoutTotals;
  chooseAddonItem: (
    addonItem: Entry<
      TypePackageAddOnItemSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >,
    checked: boolean,
  ) => void;
  isLoading?: boolean;
  onApplyPromoCode?: (promoCode?: string) => Promise<void>;
  onClearPromoCode?: () => void;
  currentPromoCode?: string;
  /** The entered code no longer validates (e.g. email edit broke a
   * builder-partner binding) — shown inline under the promo field. */
  promoCodeError?: string | null;
  checkoutConfiguration?: TypeCheckoutConfiguration<"WITHOUT_UNRESOLVABLE_LINKS">;
  /** $0 no-payment mode: add-ons are never part of a builder-funded
   * redemption, so the selection block is not offered at all. */
  hideAddOns?: boolean;
  /** False in $0 mode: the code IS the transaction — removing it is not a
   * meaningful action and the clear path would only toast an error. */
  allowPromoRemoval?: boolean;
}) {
  const { checkoutImage } = packageDetailItem?.fields || {};
  const fullPackageName = packageModificationItem
    ? packageModificationItem?.fields?.name
    : packageDetailItem?.fields.fullPackageName;
  const { product, total, subTotal, discount, taxAmount } = paymentIntent;

  const productPrice = product.price / 100;
  const totalPrice = (total / 100).toFixed(2);
  const subTotalPrice = (subTotal / 100).toFixed(2);
  const taxAmountPrice =
    taxAmount != undefined && taxAmount != null
      ? (taxAmount / 100).toFixed(2)
      : 0;
  const discountValue = discount?.discount
    ? (discount.discount / 100).toFixed(2)
    : 0;
  const contentfulAddOnInfo = packageDetailItem?.fields.addOnItems;

  const [promoCodeInput, setPromoCodeInput] = useState(currentPromoCode);
  const [isApplyingPromoCode, setIsApplyingPromoCode] = useState(false);

  const totalWithoutTax = () => {
    if (!discount?.discount) return Number(subTotalPrice);

    if (discount.type === "percentage") {
      return (
        Number(subTotalPrice) -
        (Number(discount.discount) / 100) * Number(subTotalPrice)
      );
    } else {
      return Number(subTotalPrice) - Number(discount.discount) / 100;
    }
  };

  const taxRate = taxAmount
    ? Number(((taxAmount / 100 / totalWithoutTax()) * 100).toFixed(2))
    : 0;

  const handleApplyPromoCode = async () => {
    if (!onApplyPromoCode || !promoCodeInput.trim()) return;

    setIsApplyingPromoCode(true);
    try {
      await onApplyPromoCode(promoCodeInput.trim());
      // Clear input on successful application
      setPromoCodeInput("");
    } catch (error) {
      console.error("Error applying promo code:", error);
    } finally {
      setIsApplyingPromoCode(false);
    }
  };

  const handleRemovePromoCode = () => {
    if (onClearPromoCode) {
      onClearPromoCode();
    }
  };

  return (
    <div className="w-full md:max-w-[560px]">
      <Card
        variant="default"
        className="relative mb-4 flex flex-col gap-3 p-4 drop-shadow-md"
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white bg-opacity-75">
            <Spinner />
          </div>
        )}
        <H5 className="font-display mb-4 text-left text-2xl font-normal">
          Order Summary
        </H5>
        <div className="flex flex-row justify-between gap-2">
          {checkoutImage?.fields.image?.fields.file?.url && (
            <Image
              src={`https:${checkoutImage.fields.image.fields.file.url}`}
              alt={checkoutImage?.fields.description ?? ""}
              height={74}
              width={95}
              className="h-[74px] w-[95px] rounded-lg object-cover"
            />
          )}
          <LineItem name={fullPackageName ?? ""} price={productPrice} />
        </div>

        <div>
          {productPrice >= 995 && (
            <div className="flex gap-2 py-2">
              <Input
                placeholder="Discount Code"
                className="border-system-grayscale-20 w-full rounded-md"
                value={promoCodeInput}
                onChange={(e) => setPromoCodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleApplyPromoCode();
                  }
                }}
                disabled={isApplyingPromoCode || !!currentPromoCode}
              />
              <Button
                variant="outline"
                className="border-system-grayscale-20 font-body w-[78px] font-semibold"
                onClick={handleApplyPromoCode}
                disabled={
                  isApplyingPromoCode ||
                  !promoCodeInput.trim() ||
                  !!currentPromoCode
                }
              >
                {isApplyingPromoCode ? <Spinner size="sm" /> : "Apply"}
              </Button>
            </div>
          )}
          {currentPromoCode && (
            <div className="text-brand-brown-black flex items-center justify-start gap-2 px-4 text-sm">
              <span>
                {promoCodeError
                  ? `Promo code: ${currentPromoCode}`
                  : `Promo code applied: ${currentPromoCode}`}
              </span>
              {allowPromoRemoval && (
                <button
                  onClick={handleRemovePromoCode}
                  className="text-brand-brown-black ml-2 transition-colors hover:text-red-600"
                  aria-label="Remove promo code"
                >
                  <FontAwesomeIcon icon={faTrash} className="h-7 w-7" />
                </button>
              )}
            </div>
          )}
          {promoCodeError && (
            <p className="px-4 pt-1 text-sm text-red-600" role="alert">
              {promoCodeError}
            </p>
          )}
        </div>

        {!hideAddOns &&
          contentfulAddOnInfo &&
          contentfulAddOnInfo.length > 0 &&
          paymentIntent && (
            <PackageDisplayAddOns
              currentAddOns={currentAddons}
              paymentIntent={paymentIntent}
              contentfulAddons={contentfulAddOnInfo}
              onToggleAddon={(stripeProductId, checked) => {
                const addonEntry = contentfulAddOnInfo.find(
                  (addon) => addon?.fields.stripeProductId === stripeProductId,
                );
                if (addonEntry) {
                  chooseAddonItem(addonEntry, checked);
                }
              }}
              isLoading={isLoading}
              checkoutConfiguration={checkoutConfiguration}
            />
          )}
        {discount &&
          discountValue !== 0 &&
          (discount.type === "percentage" ? (
            <LineItem
              name={"Discount"}
              price={(
                Math.round(
                  (((discount.discount / 100) * subTotal) / 100) * 100,
                ) / 100
              ).toFixed(2)}
              isDiscount
            />
          ) : (
            <LineItem name={"Discount"} price={discountValue} isDiscount />
          ))}
        <LineDivider
          className="h-2 border-black opacity-20"
          orientation="horizontal"
          variant="dotted"
        />
        {Number(taxAmountPrice) > 0 && taxRate && taxRate > 0 && (
          <LineItem
            name={`Estimated Tax (${taxRate}%)`}
            price={taxAmountPrice}
            className="text-lg"
            classNamePrice="text-lg font-normal"
          />
        )}
        <LineItem
          name={"Total"}
          price={totalPrice}
          className="text-2xl"
          classNamePrice="text-2xl font-normal"
        />
      </Card>
    </div>
  );

  function LineItem({
    name,
    price,
    className,
    classNamePrice,
    isDiscount = false,
  }: {
    name: string;
    price: number | string;
    className?: string;
    classNamePrice?: string;
    isDiscount?: boolean;
  }) {
    return (
      <div className="space-between flex w-full flex-row justify-between gap-2">
        <div
          className={cn(
            "font-display text-brand-secondary-dark text-lg",
            className,
          )}
        >
          {name}
        </div>
        <div
          className={cn(
            "font-body text-brand-secondary-dark text-lg font-semibold",
            classNamePrice,
          )}
        >
          {isDiscount ? `- $${price}` : `$${price}`}
        </div>
      </div>
    );
  }
}
